import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import http from '@lib/http-client'
import {
  ISymptom,
  ISymptomGroup,
  ISymptomTrackingEntry,
  SymptomTrackingResponse,
  SymptomsStore
} from './types'

const TRACKING_PAGE_SIZE = 200

export const useSymptomsStore = create<SymptomsStore>()(
  devtools((_set, get) => ({
    getSelectedSymptoms: async (userId: number) => {
      try {
        const response = await http.get(`/user/${userId}/symptoms/selected`)

        return Array.isArray(response) ? (response as ISymptom[]) : []
      } catch (error) {
        console.error('Error fetching selected symptoms:', error)
        return []
      }
    },

    getSymptomTracking: async (
      userId: number,
      { page = 0, page_size = TRACKING_PAGE_SIZE } = {}
    ) => {
      try {
        const params = new URLSearchParams()
        params.append('page', String(page))
        params.append('page_size', String(page_size))

        const response = await http.get(
          `/user/${userId}/symptoms/tracking?${params.toString()}`
        )

        if (!response || !Array.isArray(response.results)) {
          return null
        }

        return response as SymptomTrackingResponse
      } catch (error) {
        console.error('Error fetching symptom tracking:', error)
        return null
      }
    },

    getAllSymptomTracking: async (userId: number) => {
      const entries: ISymptomTrackingEntry[] = []
      let page = 0
      let hasNextPage = true

      while (hasNextPage) {
        const response = await get().getSymptomTracking(userId, { page })
        if (!response) {
          break
        }
        entries.push(...response.results)
        hasNextPage = response.has_next_page
        page += 1
      }

      return entries
    },

    getSymptomGroups: async (userId: number) => {
      const [selected, tracking] = await Promise.all([
        get().getSelectedSymptoms(userId),
        get().getAllSymptomTracking(userId)
      ])

      const selectedIds = new Set(selected.map(symptom => symptom.id))

      // the tracking entries are the only reliable source for the counts and
      // dates: /symptoms/available reports tracked as 0 even for symptoms that
      // do have logs. Entries come ordered by date descending, so the first one
      // of a symptom is its latest log and the last one is when it started.
      const groups = new Map<number, ISymptomGroup>()
      tracking.forEach(entry => {
        const current = groups.get(entry.symptom.id)
        groups.set(entry.symptom.id, {
          symptom: entry.symptom,
          active: selectedIds.has(entry.symptom.id),
          log_count: (current?.log_count ?? 0) + 1,
          start_date: entry.date,
          last_end_date: current?.last_end_date ?? entry.date
        })
      })

      return [...groups.values()]
    }
  }))
)
